import {
  DOCUMENT_CONTENT_TYPES,
  DOCUMENT_KINDS,
  MAX_DOCUMENT_BYTES,
  REFERRAL_URGENCIES,
  SICK_SHEET_RECOMMENDATIONS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema } from './clinical-common.ts';

// Clinical documents (docs/plans/phase1-consultation.md §3.3 C4, §7.4; C19, S4, P10, P11):
// sick sheets, referral letters and documents uploaded to a visit. Titles, remarks,
// reasons and the referral summary are PHI: they travel only in bodies.

export const CLINICAL_DOCUMENT_STATUSES = ['issued', 'entered_in_error'] as const;
export const PATIENT_DOCUMENT_STATUSES = ['active', 'entered_in_error'] as const;

/** Who marked a document entered in error, when and why; null while it stands. */
const statusChangeSchema = z
  .object({ by: actorSchema, at: z.iso.datetime(), reason: z.string() })
  .nullable();

/** The patient as printed: name, sex, age and patient number, nothing clinical. */
export const printedPatientSchema = z.object({
  name: z.string(),
  patientNumber: z.string(),
  sexDisplay: z.string(),
  birthDate: z.string().nullable(),
  birthDateEstimated: z.boolean(),
});
export type PrintedPatientView = z.infer<typeof printedPatientSchema>;

// --- Sick sheets (C19) ----------------------------------------------------------------------

export const issueSickSheetRequestSchema = z.object({
  clientRequestId: z.uuid(),
  /** The clinician's consultation in progress, when issued from one. */
  consultationId: z.uuid().nullable().default(null),
  restFrom: isoDaySchema,
  restTo: isoDaySchema,
  recommendation: z.enum(SICK_SHEET_RECOMMENDATIONS),
  /** A diagnosis of this visit to print; only with the patient's agreement (C19). */
  diagnosisId: z.uuid().nullable().default(null),
  patientAgreedToDiagnosis: z.boolean().default(false),
  remarks: z.string().trim().max(500).nullable().default(null),
});
export type IssueSickSheetRequest = z.input<typeof issueSickSheetRequestSchema>;

export const sickSheetSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  consultationId: z.uuid().nullable(),
  sickSheetNumber: z.string(),
  issuedAt: z.iso.datetime(),
  issuedBy: actorSchema,
  /** "Name, Cadre" and the licence line, as captured at issue. */
  issuerDisplay: z.string(),
  issuerLicenceDisplay: z.string().nullable(),
  restFrom: isoDaySchema,
  restTo: isoDaySchema,
  days: z.number().int().min(1),
  recommendation: z.enum(SICK_SHEET_RECOMMENDATIONS),
  diagnosisShown: z.boolean(),
  diagnosisText: z.string().nullable(),
  remarks: z.string().nullable(),
  status: z.enum(CLINICAL_DOCUMENT_STATUSES),
  enteredInError: statusChangeSchema,
  printCount: z.number().int().min(0),
});
export type SickSheetView = z.infer<typeof sickSheetSchema>;

export const sickSheetListSchema = z.object({ items: z.array(sickSheetSchema) });

export const sickSheetPrintSchema = z.object({
  sickSheet: sickSheetSchema,
  facility: z.object({ name: z.string() }),
  patient: printedPatientSchema,
  /** The copy this print will be: 1 for the first, "Copy 2" after. */
  copy: z.number().int().min(1),
});
export type SickSheetPrintView = z.infer<typeof sickSheetPrintSchema>;

// --- Referral letters (S4) ------------------------------------------------------------------

export const referralSummarySchema = z.object({
  diagnoses: z.array(
    z.object({ display: z.string(), code: z.string().nullable(), rank: z.string() }),
  ),
  allergies: z.object({
    state: z.enum(['allergies', 'no_known_allergies', 'not_recorded']),
    items: z.array(z.string()),
  }),
  vitals: z.array(z.object({ label: z.string(), value: z.string() })),
  vitalsTakenAt: z.iso.datetime().nullable(),
  medicines: z.array(z.object({ display: z.string(), directions: z.string() })),
  results: z.array(z.object({ id: z.uuid(), text: z.string(), releasedAt: z.iso.datetime() })),
  clinicianText: z.string().nullable(),
});
export type ReferralSummaryView = z.infer<typeof referralSummarySchema>;

/** What the letter could carry, for the clinician to choose results and write the summary. */
export const referralPreviewSchema = referralSummarySchema.omit({ clinicianText: true });
export type ReferralPreviewView = z.infer<typeof referralPreviewSchema>;

export const issueReferralRequestSchema = z.object({
  clientRequestId: z.uuid(),
  consultationId: z.uuid().nullable().default(null),
  /**
   * The receiving facility from the Facility Registry cache (referrals plan §1.1 item 2).
   * When set, the server takes the name and MFL code from the cache, not from the body.
   */
  registryFacilityId: z.uuid().nullable().default(null),
  toFacilityName: z.string().trim().min(1).max(200),
  /** Optional; its format is not checked until the specification is known (S4). */
  toFacilityMflCode: z.string().trim().min(1).max(20).nullable().default(null),
  toService: z.string().trim().min(1).max(200).nullable().default(null),
  urgency: z.enum(REFERRAL_URGENCIES),
  reason: z.string().trim().min(1).max(2000),
  /**
   * The released results to include, from the preview. The server builds the rest of the
   * summary from the visit itself at issue; it never takes clinical content from the client.
   */
  resultIds: z.array(z.uuid()).max(50).default([]),
  clinicianText: z.string().max(4000).default(''),
});
export type IssueReferralRequest = z.input<typeof issueReferralRequestSchema>;

export const referralLetterSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  consultationId: z.uuid().nullable(),
  referralNumber: z.string(),
  issuedAt: z.iso.datetime(),
  author: actorSchema,
  authorDisplay: z.string(),
  authorLicenceDisplay: z.string().nullable(),
  toFacilityName: z.string(),
  toFacilityMflCode: z.string().nullable(),
  toService: z.string().nullable(),
  urgency: z.enum(REFERRAL_URGENCIES),
  reason: z.string(),
  summary: referralSummarySchema,
  status: z.enum(CLINICAL_DOCUMENT_STATUSES),
  enteredInError: statusChangeSchema,
  printCount: z.number().int().min(0),
  /** The referral record the letter opened (referrals plan §2.2). */
  referralId: z.uuid().nullable(),
});
export type ReferralLetterView = z.infer<typeof referralLetterSchema>;

export const referralLetterListSchema = z.object({ items: z.array(referralLetterSchema) });

export const referralPrintSchema = z.object({
  referral: referralLetterSchema,
  facility: z.object({ name: z.string() }),
  patient: printedPatientSchema,
  copy: z.number().int().min(1),
});
export type ReferralPrintView = z.infer<typeof referralPrintSchema>;

// --- Uploaded documents (P10, P11) ----------------------------------------------------------

/** Base64 of at most 10 MB of bytes. */
const MAX_BASE64_LENGTH = Math.ceil(MAX_DOCUMENT_BYTES / 3) * 4;

/**
 * One file and what it is, in one JSON body. The bytes travel as base64 so the title and
 * kind stay in the body with them (no multipart parser; ADR 0016). The server decides the
 * type from the bytes, never from a name or a claimed type.
 */
export const uploadDocumentRequestSchema = z.object({
  clientRequestId: z.uuid(),
  kind: z.enum(DOCUMENT_KINDS),
  title: z.string().trim().min(1).max(200),
  /** The date written on the paper, when there is one. */
  documentDate: isoDaySchema.nullable().default(null),
  /** An imaging request of this visit whose report this is. */
  linkedOrderId: z.uuid().nullable().default(null),
  contentBase64: z
    .string()
    .max(MAX_BASE64_LENGTH + 4)
    .regex(/^[A-Za-z0-9+/]*={0,2}$/, 'Must be the file encoded as base64'),
});
export type UploadDocumentRequest = z.input<typeof uploadDocumentRequestSchema>;

/** The largest JSON body the upload route accepts: the base64 plus room for the rest. */
export const UPLOAD_BODY_LIMIT_BYTES = MAX_BASE64_LENGTH + 64 * 1024;

export const patientDocumentSchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  visitId: z.uuid(),
  visitNumber: z.string(),
  kind: z.enum(DOCUMENT_KINDS),
  title: z.string(),
  documentDate: isoDaySchema.nullable(),
  contentType: z.enum(DOCUMENT_CONTENT_TYPES),
  sizeBytes: z.number().int().min(1),
  linkedOrderId: z.uuid().nullable(),
  uploadedAt: z.iso.datetime(),
  uploadedBy: actorSchema,
  status: z.enum(PATIENT_DOCUMENT_STATUSES),
  enteredInError: statusChangeSchema,
});
export type PatientDocumentView = z.infer<typeof patientDocumentSchema>;

export const patientDocumentListSchema = z.object({ items: z.array(patientDocumentSchema) });
